import { FC, Fragment, useContext, useState } from 'react';
import { MoTableData, SearchRequest } from 'ts/shared/interfaces/MOResponse';
import Button from 'components/ui/Button/Button';
import Dropdown from '../Dropdown';
import { faFileText } from '@fortawesome/free-regular-svg-icons';
import { generateReport } from 'services/myOrders';
import useRecaptcha from 'hooks/useRecaptcha';
import { useTranslation } from 'react-i18next';
import { faDollar, faSpinner } from '@fortawesome/free-solid-svg-icons';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { showError } from 'utils/utils';
import withErrorBoundary from '../withErrorBoundary';
import { MoContext } from 'contexts/MoContext';
import { sortTableData } from 'utils/shared/utils';
import MoReportModal from './MoReportModal';
import { MainContext } from 'contexts/MainContext';

interface MoTableReportProps {
  searchParams: SearchRequest;
  table: MoTableData[];
}

const MoTableReport: FC<MoTableReportProps> = ({ searchParams, table }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { handleRecapcha } = useRecaptcha();
  const { t } = useTranslation();
  const [reportType, setReportType] = useState('PDF');
  const [reportPrice, setReportPrice] = useState('0');
  const { user } = useContext(MainContext);

  const { sort, visibleRows, activeOrder } = useContext(MoContext);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportResultUrl, setReportResultUrl] = useState('');
  const [menuCloseDate, setMenuCloseDate] = useState<Date>();

  const getReportPrices = () => {
    let reportPrices = [{ value: '0', label: 'mo.public_price' }];

    if (user?.allowSysTotalAgent) {
      reportPrices.push({ value: '1', label: 'mo.sys_total_agent' });
    }

    if (user?.allowSysNettOperator) {
      reportPrices.push({ value: '2', label: 'mo.sys_nett_operator' });
    }

    return reportPrices;
  };

  const reportTypes = [
    { value: 'PDF', label: 'PDF' },
    { value: 'XLSX', label: 'XLSX' },
    { value: 'CSV', label: 'CSV' },
  ];

  const onClickPrint = async (reportView: string) => {
    if (isLoading) return;

    setMenuCloseDate(new Date());
    setIsLoading(true);

    const token = await handleRecapcha();
    if (token) {
      const sortedTable = sortTableData(table, sort.field, sort.direction);
      const segments = sortedTable
        .filter((item: MoTableData) => visibleRows.includes(item.segmentId))
        .map((item: MoTableData) => item.segmentId)
        .join(',');

      const params = {
        recaptchaToken: token,
        reportType,
        reportView,
        segments,
        reportPrice,
      };

      const data = await generateReport(searchParams, params);

      if (data) {
        setReportResultUrl(data.pdfUrl);
        setIsReportModalOpen(true);
      } else {
        showError(t('something_went_wrong'));
      }
    }

    setIsLoading(false);
  };

  return (
    <div className="c-dropdown-concat mo-report">
      <Dropdown
        byClick={true}
        top={36}
        right={0}
        left={-195}
        openerEmpty={true}
        closeOnClickDate={menuCloseDate}
        opener={
          <Button
            title={''}
            className="btn btn-default btn-ssm"
            icon={isLoading ? faSpinner : faFileText}
            disabled={isLoading || !table.length}
          />
        }
      >
        <div className="c-dropdown-row nounderline" onClick={() => onClickPrint('OLR_1')}>
          1. {t('mo.order_list_report')}
        </div>
        <div className="c-dropdown-row nounderline" onClick={() => onClickPrint('OLR_1_1')}>
          1.1 {t('mo.order_list_report_no_names')}
        </div>
        <div className="c-dropdown-row nounderline" onClick={() => onClickPrint('OLR_2')}>
          2. {t('mo.order_list_report_by_param')}
        </div>
        <div className="c-dropdown-row " onClick={() => onClickPrint('OLR_3')}>
          3. {t('mo.order_list_report_by_dest')}
        </div>
        <div className="c-dropdown-row nounderline" onClick={() => onClickPrint('SR_4')}>
          4. {t('mo.summary_report_by_department')}
        </div>
        <div className="c-dropdown-row nounderline" onClick={() => onClickPrint('SR_5')}>
          5. {t('mo.summary_report_by_branch')}
        </div>
        <div className="c-dropdown-row " onClick={() => onClickPrint('SR_6')}>
          6. {t('mo.summary_report_by_supplier')}
        </div>
        <div className="c-dropdown-row nounderline" onClick={() => onClickPrint('OBB_7')}>
          7.{t('mo.orders_by_branch')}
        </div>
        <div className="c-dropdown-row nounderline" onClick={() => onClickPrint('OBC_8')}>
          8. {t('mo.orders_by_currency')}
        </div>
        <div className="c-dropdown-row " onClick={() => onClickPrint('OBS_9')}>
          9. {t('mo.orders_by_supplier')}
        </div>
        <div className="c-dropdown-row nounderline" onClick={() => onClickPrint('ETF_10')}>
          10. {t('mo.export_to_file')}
        </div>
        <div className="c-dropdown-row nounderline" onClick={() => onClickPrint('ETF_10_1')}>
          10.1. {t('mo.export_to_file_no_names')}
        </div>
        <div className="c-dropdown-row " onClick={() => onClickPrint('ETF_10_2')}>
          10.2. {t('mo.export_to_file_refund')}
        </div>
        {user?.allowRefundReport && (
          <Fragment>
            <div className="c-dropdown-row nounderline" onClick={() => onClickPrint('RR_11')}>
              11. {t('mo.refund_report')}
            </div>
            <div className="c-dropdown-row " onClick={() => onClickPrint('CR_12')}>
              12. {t('mo.coupon_report')}
            </div>
          </Fragment>
        )}

        <div className="ph-15 pb-10 mt-10">
          {user?.allowShowNet && (
            <div className="mb-10 ">
              <SelectCustom
                options={getReportPrices()}
                value={reportPrice}
                onChange={setReportPrice}
                movablePlaceholder={t('mo.report_price')}
                movablePlaceholderIcon={faDollar}
                needToTranslate
              />
            </div>
          )}
          <div className="">
            <SelectCustom
              options={reportTypes}
              value={reportType}
              onChange={setReportType}
              movablePlaceholder={t('mo.report_type')}
              movablePlaceholderIcon={faFileText}
              menuPlacement="top"
            />
          </div>
        </div>
      </Dropdown>
      {activeOrder?.orderRow && (
        <MoReportModal
          url={reportResultUrl}
          orderRow={activeOrder?.orderRow}
          onClose={() => setIsReportModalOpen(false)}
          isOpen={isReportModalOpen}
        />
      )}
    </div>
  );
};

export default withErrorBoundary(MoTableReport, 'MoTableReport');
